import React, { useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress,
  Tooltip,
  Container
} from '@mui/material';
import { themeColors } from '../styles/theme';
import Fonts from './Fonts';
import { useNavigate } from 'react-router-dom';
import TopNav from './TopNav';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Footer } from './Footer';
import { 
  trackAreaSearch, 
  trackNearMeSearch, 
  trackMapPinPlaced, 
  trackSearchAreaSelectionMethod
} from '../utils/analytics';

// Import leaflet and CSS
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Set up Leaflet icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface SearchAreaProps {
  setTitle: React.Dispatch<React.SetStateAction<string>>;
  setLatitude: React.Dispatch<React.SetStateAction<number>>;
  setLongitude: React.Dispatch<React.SetStateAction<number>>;
  setShowBurgerMenuIcon: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

// Get user coordinates from localStorage or use defaults
const getUserCoordinates = () => {
  const locationPermission = localStorage.getItem('locationPermissionGranted');
  const storedLat = localStorage.getItem('userLatitude');
  const storedLon = localStorage.getItem('userLongitude');
  
  if (locationPermission === 'true' && storedLat && storedLon) {
    console.log('Using stored coordinates from localStorage:', storedLat, storedLon);
    return {
      latitude: parseFloat(storedLat),
      longitude: parseFloat(storedLon)
    };
  }
  
  // Fallback to London if no stored coordinates
  return {
    latitude: 51.5074,
    longitude: -0.1278
  };
};

// Get default location (use user's coordinates if available)
const defaultCoords = getUserCoordinates();
const DEFAULT_LATITUDE = defaultCoords.latitude;
const DEFAULT_LONGITUDE = defaultCoords.longitude;
const DEFAULT_ZOOM = 13;

// Fixed radius - always 1km
const FIXED_RADIUS = 1000; // 1000m = 1km

const SearchArea: React.FC<SearchAreaProps> = ({ 
  setTitle, 
  setLatitude, 
  setLongitude, 
  setShowBurgerMenuIcon, 
  setLoading 
}) => {
  const navigate = useNavigate();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  
  const [mapInitialized, setMapInitialized] = useState(false);
  const [locationMarked, setLocationMarked] = useState(false);
  const [customLatitude, setCustomLatitude] = useState(DEFAULT_LATITUDE);
  const [customLongitude, setCustomLongitude] = useState(DEFAULT_LONGITUDE);
  const [userLatitude, setUserLatitude] = useState<number | null>(null);
  const [userLongitude, setUserLongitude] = useState<number | null>(null);
  const [loadingUserLocation, setLoadingUserLocation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set up page title and navigation elements
  useEffect(() => {
    setTitle('Search Area');
    setShowBurgerMenuIcon(true);
  }, [setTitle, setShowBurgerMenuIcon]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) return;
    
    // Check if we have saved search area parameters
    const savedParams = loadSearchAreaParams();
    const startLat = savedParams?.latitude || DEFAULT_LATITUDE;
    const startLng = savedParams?.longitude || DEFAULT_LONGITUDE;
    
    setCustomLatitude(startLat);
    setCustomLongitude(startLng);
    
    // Clean up previous map instance
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
    }
    
    try {
      // Create map
      const map = L.map(mapContainerRef.current, {
        attributionControl: false,
        zoomControl: true,
        scrollWheelZoom: true
      }).setView([startLat, startLng], DEFAULT_ZOOM);
      
      mapRef.current = map;
      
      // Add tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);
      
      // If we have saved parameters, add marker and circle
      if (savedParams) {
        addMarkerWithCircle(startLat, startLng);
        setLocationMarked(true);
      } 
      // If we're using user coordinates from localStorage, add marker and circle
      else if (startLat === DEFAULT_LATITUDE && startLng === DEFAULT_LONGITUDE && 
               localStorage.getItem('locationPermissionGranted') === 'true') {
        addMarkerWithCircle(startLat, startLng);
        setLocationMarked(true);
        
        // Track that we're using user's stored location
        trackSearchAreaSelectionMethod('stored_location', startLat, startLng);
      }
      
      // Add click handler to map
      map.on('click', handleMapClick);
      
      // Invalidate size to ensure proper rendering
      map.invalidateSize();
      
      // Set map as initialized
      setMapInitialized(true);
      
    } catch (error) {
      console.error('Error initializing map:', error);
      setError('Failed to initialize map');
    }
    
    // Clean up on unmount
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        circleRef.current = null;
      }
    };
  }, []);

  // Add marker and circle to map
  const addMarkerWithCircle = (lat: number, lng: number) => {
    if (!mapRef.current) return;
    
    // Remove existing marker and circle
    if (markerRef.current) {
      markerRef.current.remove();
    }
    if (circleRef.current) {
      circleRef.current.remove();
    }
    
    // Add marker
    const marker = L.marker([lat, lng]).addTo(mapRef.current);
    markerRef.current = marker;
    
    // Add circle
    const circle = L.circle([lat, lng], {
      radius: FIXED_RADIUS,
      fillColor: themeColors.orange,
      fillOpacity: 0.2,
      color: themeColors.orange,
      weight: 2
    }).addTo(mapRef.current);
    circleRef.current = circle;
    
    // Update coordinates
    setCustomLatitude(lat);
    setCustomLongitude(lng);
    
    // Save search area parameters to localStorage
    saveSearchAreaParams(lat, lng);
  };

  // Handle map click to add/move marker
  const handleMapClick = (e: L.LeafletMouseEvent) => {
    const { lat, lng } = e.latlng;
    
    // Track when user places a marker manually on the map
    trackMapPinPlaced(lat, lng);
    
    // Track selection method
    trackSearchAreaSelectionMethod('map_click', lat, lng);
    
    addMarkerWithCircle(lat, lng);
    setLocationMarked(true);
  };

  // Handle use current location button
  const handleUseCurrentLocation = () => {
    setLoadingUserLocation(true);
    setError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLatitude(latitude);
        setUserLongitude(longitude);
        
        // Track location usage for search area
        trackNearMeSearch(latitude, longitude);
        
        // Track selection method
        trackSearchAreaSelectionMethod('current_location', latitude, longitude);
        
        // Update map view and add marker
        if (mapRef.current) {
          mapRef.current.setView([latitude, longitude], DEFAULT_ZOOM);
          addMarkerWithCircle(latitude, longitude);
          setLocationMarked(true);
        }
        
        setLoadingUserLocation(false);
      },
      (error) => {
        console.error('Error getting user location:', error);
        setError('Failed to get your location. Please allow location access or select a location manually.');
        setLoadingUserLocation(false);
      }
    );
  };

  // Handle search button click
  const handleSearch = () => {
    if (!locationMarked) {
      setError('Please select a location on the map first');
      return;
    }
    
    // Track the search area event with location and radius
    trackAreaSearch(customLatitude, customLongitude, FIXED_RADIUS);
    
    // Set the app-wide latitude and longitude to our custom values
    setLatitude(customLatitude);
    setLongitude(customLongitude);
    
    // Store search mode in localStorage
    localStorage.setItem('searchMode', 'custom');
    
    // Navigate to the pubs page
    setLoading(true);
    navigate('/pubs');
  };

  // Save search area parameters to localStorage
  const saveSearchAreaParams = (lat: number, lng: number) => {
    const params = {
      latitude: lat,
      longitude: lng
    };
    localStorage.setItem('searchAreaParams', JSON.stringify(params));
  };

  // Load search area parameters from localStorage
  const loadSearchAreaParams = () => {
    const savedParams = localStorage.getItem('searchAreaParams');
    if (savedParams) {
      try {
        return JSON.parse(savedParams);
      } catch (error) {
        console.error('Error parsing saved search area parameters:', error);
      }
    }
    return null;
  };

  // Clear search area parameters from localStorage
  const clearSearchAreaParams = () => {
    localStorage.removeItem('searchAreaParams');
    localStorage.removeItem('searchMode');
  };

  // Handle back button click
  const handleBack = () => {
    navigate('/');
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100vh' }}>
      <TopNav title="Search Area" />
      
      <Container 
        maxWidth="lg" 
        sx={{ 
          pt: '20px',
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Fonts />
        
        <Box sx={{ 
          width: '100%',
          maxWidth: 800,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
          
          {error && (
            <Typography 
              variant="body2" 
              sx={{ color: 'error.main', textAlign: 'center', mb: 1 }}
            >
              {error}
            </Typography>
          )}
          
          <Paper 
            elevation={3} 
            sx={{ 
              width: '100%',
              height: '45vh',
              minHeight: '300px',
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative',
              mb: 1
            }}
          >
            <div 
              ref={mapContainerRef} 
              style={{ 
                width: '100%', 
                height: '100%',
                backgroundColor: '#f0f0f0',
                position: 'relative',
                zIndex: 1,
                display: 'block'
              }}
            />
            
            {!mapInitialized && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                backgroundColor: 'rgba(255,255,255,0.7)'
              }}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6">Initializing Map...</Typography>
              </Box>
            )}
          </Paper>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1,
            p: 1.5,
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(16px)',
            borderRadius: { xs: '12px', sm: '16px' },
            boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              boxShadow: '0 6px 15px rgba(0,0,0,0.15)',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 'medium' }}>
                Search Radius:
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {FIXED_RADIUS}m
              </Typography>
              <Tooltip title="Search radius is fixed to 1km">
                <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Tooltip>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
              <Button
                variant="outlined"
                startIcon={<MyLocationIcon />}
                onClick={handleUseCurrentLocation}
                disabled={loadingUserLocation}
                sx={{
                  borderColor: themeColors.blue,
                  color: themeColors.blue,
                  flex: 1,
                  '&:hover': {
                    borderColor: themeColors.blue,
                    backgroundColor: `${themeColors.blue}10`,
                  },
                  py: 0.75,
                  fontSize: '0.875rem'
                }}
              >
                {loadingUserLocation ? 'Getting Location...' : 'Use My Location'}
              </Button>
              
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={!locationMarked}
                sx={{
                  backgroundColor: themeColors.orange,
                  color: '#fff',
                  flex: 1,
                  '&:hover': {
                    backgroundColor: themeColors.mainYellow,
                  },
                  py: 0.75,
                  fontSize: '0.875rem'
                }}
              >
                Search This Area
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
      
      {/* Add Footer */}
      <Box sx={{ px: '5%' }}>
        <Footer />
      </Box>
    </Box>
  );
};

export default SearchArea;